import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./OfficeMap.css";

const OFFICES = [
  { city: "Nairobi", country: "Kenya", coords: [-1.2921, 36.8219] },
  { city: "DIFC, Dubai", country: "United Arab Emirates", coords: [25.211, 55.2796] },
  { city: "Delhi", country: "India", coords: [28.6139, 77.209] },
  { city: "Johannesburg", country: "South Africa", coords: [-26.2041, 28.0473] },
  { city: "Lagos", country: "Nigeria", coords: [6.5244, 3.3792] },
];

const FOREST = "#2D5016";
const GOLD = "#C9A84C";

const pinIcon = L.divIcon({
  className: "amadi-office-pin",
  html: `<span style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;background:${GOLD};border:2px solid ${FOREST};border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,0.55);"><span style="width:6px;height:6px;background:${FOREST};border-radius:50%;transform:rotate(45deg);"></span></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -22],
});

export function OfficeMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    const markers = OFFICES.map((office) =>
      L.marker(office.coords, { icon: pinIcon })
        .addTo(map)
        .bindTooltip(
          `<span style="font-family:Georgia,serif;font-size:13px;color:${GOLD};">${office.city}</span><br/><span style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(249,249,248,0.6);">${office.country}</span>`,
          { direction: "top", offset: [0, -20], opacity: 1, className: "amadi-office-tooltip" }
        )
    );

    const bounds = L.latLngBounds(markers.map((m) => m.getLatLng()));
    map.fitBounds(bounds, { padding: [50, 50] });

    // Re-fit on resize to keep all offices in view
    const handleResize = () => {
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [50, 50] });
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div data-testid="office-map" className="amadi-dark-map">
      <div
        ref={containerRef}
        data-testid="office-map-canvas"
        className="w-full aspect-[16/9] border border-gold/25 z-0"
        style={{ minHeight: "260px", background: "#12200A" }}
      />
      <ul
        data-testid="office-map-legend"
        className="mt-5 flex flex-wrap gap-x-6 gap-y-2"
      >
        {OFFICES.map((office) => (
          <li
            key={office.city}
            data-testid={`office-legend-${office.city.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className="flex items-center gap-2"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-forest ring-2 ring-gold/60" />
            <span className="font-serif text-sm text-charcoal">{office.city}</span>
            <span className="font-sans text-[10px] tracking-[0.14em] uppercase text-charcoal/40">
              {office.country}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
